import styles from './MarketHealth.module.css';

/**
 * Reusable Metric / Strategy / SPY comparison table shared by the Market
 * Health cards (Strategy chart, Strategy Performance, Drawdown from Peak).
 *
 * Presentational only — it takes pre-formatted string cells and renders the
 * existing `.strategyTable` CSS grid (same look the Strategy card's summary
 * table always had). Each consuming card decides:
 *
 *   - which ROWS to show, via `strategyStats.js`'s row builders
 *     (buildFullStatRows / buildReturnStatRows / buildDrawdownStatRows), tuned
 *     to that card's purpose; and
 *   - the COLUMN headers, via `strategyLabel` / `benchmarkLabel` (defaulting to
 *     the historical "Strategy" / "SPY Buy & Hold"), so a card can relabel the
 *     comparison if its framing differs.
 *
 * Rows are `{ label, strategy, spy }` objects (already run through the right
 * formatter upstream). The grid is a 3-column `.strategyTable`: label +
 * strategy value + benchmark value. The benchmark column is rendered muted,
 * matching the prior inline table's `strategyValueMuted` treatment.
 *
 * `lead` (optional) renders a paragraph of framing copy above the table (the
 * Strategy Performance card's "N trades since…" line); `children` (optional)
 * render below the table (e.g. the "when this model lags" callout).
 */
const PerformanceStatsTable = ({
  rows = [],
  strategyLabel = 'Strategy',
  benchmarkLabel = 'SPY Buy & Hold',
  lead = null,
  className,
  children,
}) => {
  if (rows.length === 0) return null;

  return (
    <div className={className ?? styles.strategySummary}>
      {lead}
      <div className={styles.strategyTable}>
        <span className={styles.strategyHead}>Metric</span>
        <span className={`${styles.strategyHead} ${styles.strategyHeadValue}`}>{strategyLabel}</span>
        <span className={`${styles.strategyHead} ${styles.strategyHeadValue}`}>{benchmarkLabel}</span>

        {rows.map((row) => (
          <RowCells key={row.label} row={row} />
        ))}
      </div>
      {children}
    </div>
  );
};

// A row is three grid cells (label + two values). Split out so the `.map`
// stays a flat list of grid children — the `.strategyTable` grid places every
// child into the next cell, so wrapping a row in its own element would break
// the 3-column flow. React fragments emit no DOM element, preserving that flow.
const RowCells = ({ row }) => (
  <>
    <span className={styles.strategyLabel}>{row.label}</span>
    <strong className={styles.strategyValue}>{row.strategy}</strong>
    <strong className={`${styles.strategyValue} ${styles.strategyValueMuted}`}>{row.spy}</strong>
  </>
);

export default PerformanceStatsTable;
